"use client";

import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { searchArtists, createArtist } from "@/services/artists";
import type { ArtistProfile } from "@/services/artists";
import { Check, Plus, Search } from "lucide-react";
import { cn } from "@/lib/utils";

interface ArtistInputProps {
  id?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string, artist?: ArtistProfile) => void;
  placeholder?: string;
  onArtistSelect?: (artist: ArtistProfile) => void;
  className?: string;
  /** When false, hides the "Add to Database" option and never calls createArtist.
   *  Use this in contributor name fields where you only want to link existing
   *  artists / users, not accidentally create a new artist record. Default: true */
  allowCreate?: boolean;
}

export function ArtistInput({
  id = "mediaArtist",
  value: externalValue,
  defaultValue = "",
  onChange,
  placeholder = "Your artist name or collaborators",
  onArtistSelect,
  className,
  allowCreate = true,
}: ArtistInputProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<ArtistProfile[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const value = externalValue !== undefined ? externalValue : internalValue;

  const handleChangeValue = (newValue: string) => {
    setInternalValue(newValue);
    onChange?.(newValue);
    // Open dropdown as soon as there are ≥2 characters; close it when cleared
    setIsOpen(newValue.trim().length > 1);
  };

  // Search for artists as user types
  useEffect(() => {
    const delayTimer = setTimeout(async () => {
      if (value.trim().length > 1) {
        setIsSearching(true);
        try {
          const results = await searchArtists(value);
          setSearchResults(results);
          setSelectedIndex(-1);
        } catch (error) {
          console.error("Error searching artists:", error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(delayTimer);
  }, [value]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && e.key === "ArrowDown") {
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < searchResults.length ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && searchResults[selectedIndex]) {
          handleSelectArtist(searchResults[selectedIndex]);
        } else if (value.trim() && allowCreate) {
          handleCreateArtist();
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSelectArtist = (artist: ArtistProfile) => {
    handleChangeValue(artist.name);
    setIsOpen(false);
    setSelectedIndex(-1);
    onArtistSelect?.(artist);
  };

  const handleCreateArtist = async () => {
    if (!value.trim() || isCreating) return;

    // Check if artist already exists
    const existing = searchResults.find(
      (a) => a.name.toLowerCase() === value.toLowerCase()
    );
    if (existing) {
      handleSelectArtist(existing);
      return;
    }

    setIsCreating(true);
    try {
      const newArtist = await createArtist({
        name: value.trim(),
        genres: [],
        bio: `Artist: ${value.trim()}`,
      });

      if (newArtist) {
        handleSelectArtist(newArtist);
      }
    } catch (error) {
      console.error("Error creating artist:", error);
    } finally {
      setIsCreating(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const showDropdown = isOpen && value.trim().length > 1;
  const hasResults = searchResults.length > 0;

  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        id={id}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => handleChangeValue(e.target.value)}
        onFocus={() => value.trim().length > 1 && setIsOpen(true)}
        onKeyDown={handleKeyDown}
        className={cn("pr-10", className)}
      />

      {isSearching && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
        </div>
      )}

      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border border-border rounded-md shadow-lg max-h-64 overflow-y-auto"
        >
          {/* Search results */}
          {hasResults && (
            <div className="p-1">
              {searchResults.map((artist, index) => (
                <button
                  key={artist.id}
                  onClick={() => handleSelectArtist(artist)}
                  className={cn(
                    "w-full px-3 py-2 text-left text-sm rounded flex items-center gap-2 transition-colors",
                    selectedIndex === index
                      ? "bg-foreground text-background"
                      : "hover:bg-muted text-foreground"
                  )}
                >
                  <Check className="h-4 w-4 opacity-0" />
                  <div className="flex-1">
                    <div className="font-medium">{artist.name}</div>
                    {artist.genres.length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        {artist.genres.join(", ")}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Add new artist option — only when allowCreate is true */}
          {allowCreate && value.trim() && (
            <div className="border-t border-border p-1">
              <button
                onClick={handleCreateArtist}
                disabled={isCreating}
                className={cn(
                  "w-full px-3 py-2 text-left text-sm rounded flex items-center gap-2 transition-colors",
                  selectedIndex === searchResults.length
                    ? "bg-foreground text-background"
                    : "hover:bg-muted text-foreground",
                  isCreating && "opacity-50 cursor-not-allowed"
                )}
              >
                <Plus className="h-4 w-4" />
                <div className="flex-1">
                  <div className="font-medium">
                    {isCreating ? "Creating..." : "Add to Database"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    &ldquo;{value.trim()}&rdquo; as new artist
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* No results message */}
          {!hasResults && value.trim() && (
            <div className="px-3 py-2 text-sm text-muted-foreground text-center">
              {allowCreate
                ? `No artists found. Create "${value.trim()}" to add it to the database.`
                : `No matching artists found for "${value.trim()}".`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
